import { graphql } from 'gatsby';

import { Grid2 as Grid, styled } from '@mui/material';

import { MewaAdvantagesIcon } from '@mewa/types';
import {
  IconGrid,
  IconGridWithTitle,
  MarkdownContainer,
  mewaAdvantagesIconMap,
  ResponsiveGrid,
} from '@mewa/ui';

import { ComponentProps, PossibleContentSections } from '../fragment-and-types';
import { ContentLivePreviewConfig } from '../live-preview/types';
import { LabeledIcon } from './labeled-icon';

export const IconGridFragment = graphql`
  fragment IconGridFragment on STRAPI__COMPONENT_SHARED_ICON_GRID {
    title: Title
    description: Description
    iconList: IconList {
      icons: Icons {
        ...LabeledIconFragment
      }
    }
    addLeftSpacing: AddLeftSpacing
    strapi_component
  }
`;

export const componentName = 'shared.icon-grid';

export type IconGridData = {
  title: string | null;
  addLeftSpacing?: boolean;
  description: string | null;
  iconList: {
    icons: LabeledIcon[];
  } | null;
  strapi_component: typeof componentName;
};

export type IconGridPreview = {
  strapi_component: typeof componentName;
  Title: string;
  Description: string;
  IconList: {
    Icons: { icon: MewaAdvantagesIcon; subtitle: string; title: string }[];
  };
  AddLeftSpacing: boolean;
};

export const isIconGrid = (
  component: PossibleContentSections,
): component is IconGridData => {
  return component.strapi_component === componentName;
};

export const StrapiIconGrid = ({
  iconList,
  addLeftSpacing,
  description,
  title,
}: ComponentProps<IconGridData>) => {
  const iconProps =
    iconList?.icons.map((icon) => ({
      title: icon.title,
      subtitle: icon.subtitle,
      Icon: mewaAdvantagesIconMap[icon.icon],
    })) ?? [];

  return (
    <Wrapper>
      {(title || description) && (
        <ResponsiveGrid>
          <Grid
            offset={{
              xs: addLeftSpacing ? 12 : 0,
              md: addLeftSpacing ? 3 : 0,
            }}
            size={{
              xs: 12,
              md: 8,
            }}
          >
            <HeaderSection>
              {title && <Headline>{title}</Headline>}
              {description && <Description html={description} />}
            </HeaderSection>
          </Grid>
        </ResponsiveGrid>
      )}
      {iconList &&
        (addLeftSpacing ? (
          <IconGridWithTitle icons={iconProps} />
        ) : (
          <StyledIconGrid icons={iconProps} />
        ))}
    </Wrapper>
  );
};

const StyledIconGrid = styled(IconGrid)({
  marginTop: '-35px',
});

const Wrapper = styled('div')({
  marginBottom: '50px',
});

const HeaderSection = styled('div')(({ theme }) => ({
  maxWidth: '845px',
  marginBottom: '120px',

  [theme.breakpoints.down('md')]: {
    marginBottom: '65px',
  },
}));

const Headline = styled('h2')(({ theme }) => ({
  ...theme.typography.h4,
  marginTop: 0,
  marginBottom: '40px',
}));

const Description = styled(MarkdownContainer)({
  marginBottom: '0 !important',
});

export const config = {
  isComponent: isIconGrid,
  Component: StrapiIconGrid,
  componentName,
};

export const previewConfig: ContentLivePreviewConfig = {
  populationParams: { IconList: { populate: '*' } },
  componentName,
  mapProperties: (iconGrid): IconGridData | null =>
    iconGrid.strapi_component === componentName
      ? {
          ...iconGrid,
          title: iconGrid.Title,
          description: iconGrid.Description,
          iconList: {
            icons: iconGrid.IconList?.Icons ?? [],
          },
          addLeftSpacing: iconGrid.AddLeftSpacing,
        }
      : null,
};
